import {useTranslations} from 'use-intl'
import {useMemo} from 'react'
import clsx from 'clsx'
import {ProductWithIds} from '~/client/spreadshop/missing-types'
import ImageComposition from '~/components/ImageComposition'
import useShopConfig from '~/hooks/useShopConfig'
import {Image, ImageFormat} from '~/gql/graphql'

type DesignImageProps = {
    className?: string
    product: ProductWithIds
}

const DesignImage = ({className, product}: DesignImageProps) => {
    const t = useTranslations('productDetails.designDialog')
    const {imageServerEndpoint} = useShopConfig()

    const image = useMemo<Image | undefined>(() => {
        const vId = product.vpId
        const viewId = product.defaultViewId

        if (!vId || !viewId) {
            return undefined
        }

        const imageUrl = new URL(
            `${imageServerEndpoint}/compositions/${vId}/views/${viewId ?? '1'}.webp`
        )
        imageUrl.searchParams.set('noPt', 'true')
        imageUrl.searchParams.set('backgroundColor', 'ffffff')

        return {
            src: imageUrl.toString(),
            srcset: [100, 400, 600]
                .map((size) => {
                    imageUrl.searchParams.set('height', String(size))
                    return `${imageUrl.toString()} ${size}w`
                })
                .join(','),
            altText: t('designImageAlt', {design: product.name}),
            format: ImageFormat.Png,
        }
    }, [imageServerEndpoint, product.vpId, product.defaultViewId, product.name, t])

    if (!image) {
        return null
    }

    return (
        <ImageComposition
            className={clsx(className, '!aspect-auto flex justify-center items-center h-60')}
            imageClassName="!w-auto"
            image={image}
            sizes="(min-width: 640px) 1000px, 500px"
        />
    )
}

export default DesignImage
